/**
 * Kiti Cymru — Load More for collection pages
 * Pairs with snippets/load-more.liquid and assets/kiti-load-more.css.
 *
 * Behaviour:
 *   1. Intercepts clicks on [data-load-more-button] via document-level
 *      event delegation, so the handler survives Symmetry's filter AJAX
 *      (which replaces innerHTML of every [data-ajax-container] without
 *      firing a hookable event).
 *   2. Fetches the next paginated page, parses the response, extracts
 *      new product cards from the grid identified by data-grid-selector,
 *      and appends them.
 *   3. Updates the browser URL via History API so back/forward and
 *      deep-linking continue to work.
 *   4. Updates the button to point at the page-after-next, or hides
 *      it when no further pages exist.
 *   5. Fires a 'kiti:collection:loadmore' CustomEvent for analytics.
 *
 * Fallbacks:
 *   - JS disabled: the button is a real <a href> link, plus the
 *     theme's native paginator is rendered inside a <noscript> tag.
 *   - Fetch error: the button reverts to "Try again" and a second
 *     click navigates natively to the next page URL.
 */
(function () {
  'use strict';

  var SELECTORS = {
    container: '[data-load-more]',
    button: '[data-load-more-button]',
    label: '[data-load-more-label]',
    count: '[data-load-more-count]',
    status: '[data-load-more-status]',
    freshCard: '[data-kiti-load-more-fresh]'
  };

  var LABELS = {
    idle: 'Load more products',
    loading: 'Loading...',
    error: 'Try again',
    done: 'All products loaded'
  };

  // -----------------------------------------------------------------
  // Click handler — event delegation.
  // -----------------------------------------------------------------
  // We bind once on document, then match clicks against the button
  // selector. This survives any DOM replacement (Symmetry's filter
  // AJAX, theme editor section reloads, etc.) without needing to
  // re-init on each swap.
  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest
      ? event.target.closest(SELECTORS.button)
      : null;
    if (!button) return;

    var container = button.closest(SELECTORS.container);
    if (!container) return;

    event.preventDefault();

    handleClick({
      container: container,
      button: button,
      gridSelector: container.getAttribute('data-grid-selector') || '[data-load-more-grid]',
      totalPages: parseInt(container.getAttribute('data-total-pages'), 10) || 0
    });
  });

  function handleClick(ctx) {
    if (ctx.button.getAttribute('aria-busy') === 'true') return;

    var nextUrl = ctx.button.getAttribute('href');
    if (!nextUrl) return;

    setLoading(ctx.button, true);

    fetch(nextUrl, { headers: { 'Accept': 'text/html' }, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var currentGrid = document.querySelector(ctx.gridSelector);
        var nextGrid = doc.querySelector(ctx.gridSelector);
        if (!currentGrid || !nextGrid) {
          throw new Error('Grid not found using selector: ' + ctx.gridSelector);
        }

        // Filter to product cards only. Symmetry's grid intersperses
        // promotion tiles (<div class="product-grid__promo-tile">) with
        // product cards based on block position. Promo tiles are intended
        // for page 1 only — appending them again from page 2+ would
        // duplicate them in the grid. Product cards are <product-block>
        // custom elements with a data-product-id attribute.
        var newCards = Array.prototype.slice.call(nextGrid.children).filter(function (el) {
          if (!el || el.nodeType !== 1) return false;
          return el.matches && el.matches('product-block, [data-product-id]');
        });
        var fragment = document.createDocumentFragment();
        newCards.forEach(function (card) {
          // Mark every appended card so we can detect appended state
          // later (popstate handler, fresh-card focus styles).
          card.setAttribute('data-kiti-load-more-fresh', 'true');
          fragment.appendChild(card);
        });

        currentGrid.appendChild(fragment);

        // Update browser URL without reloading.
        try {
          window.history.pushState({ kitiLoadMore: true }, '', nextUrl);
        } catch (e) {
          // Ignore — non-fatal.
        }

        // Update the Load More container from the fetched page.
        var nextContainer = doc.querySelector(SELECTORS.container);
        var nextButton = nextContainer ? nextContainer.querySelector(SELECTORS.button) : null;
        var nextCount = nextContainer ? nextContainer.querySelector(SELECTORS.count) : null;
        var currentCount = ctx.container.querySelector(SELECTORS.count);

        if (currentCount && nextCount) {
          currentCount.textContent = nextCount.textContent;
        }

        if (nextButton && nextButton.getAttribute('href')) {
          // More pages remain.
          ctx.button.setAttribute('href', nextButton.getAttribute('href'));
          if (nextContainer) {
            ctx.container.setAttribute('data-current-page', nextContainer.getAttribute('data-current-page'));
          }
          setLoading(ctx.button, false);
        } else {
          // No more pages.
          setDone(ctx);
        }

        // Move focus to the first new card for keyboard users.
        if (newCards[0]) {
          var focusable = newCards[0].querySelector('a, button');
          if (focusable) {
            focusable.setAttribute('tabindex', '-1');
            focusable.focus({ preventScroll: true });
          }
        }

        // Analytics hook.
        var loadedPage = parsePage(nextUrl);
        document.dispatchEvent(new CustomEvent('kiti:collection:loadmore', {
          detail: {
            page: loadedPage,
            productCount: newCards.length,
            totalPages: ctx.totalPages,
            url: nextUrl
          }
        }));
      })
      .catch(function (error) {
        if (window.console && console.error) {
          console.error('[kiti-load-more]', error);
        }
        setError(ctx.button);
        // The href is preserved, so a second click navigates natively.
      });
  }

  function setLoading(button, isLoading) {
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    button.classList.toggle('is-loading', isLoading);
    button.classList.remove('has-error');
    var label = button.querySelector(SELECTORS.label);
    if (label) label.textContent = isLoading ? LABELS.loading : LABELS.idle;
  }

  function setError(button) {
    button.setAttribute('aria-busy', 'false');
    button.classList.remove('is-loading');
    button.classList.add('has-error');
    var label = button.querySelector(SELECTORS.label);
    if (label) label.textContent = LABELS.error;
  }

  function setDone(ctx) {
    ctx.button.setAttribute('aria-busy', 'false');
    ctx.button.classList.remove('is-loading');
    ctx.button.hidden = true;
    var status = ctx.container.querySelector(SELECTORS.status);
    if (status) {
      var total = ctx.container.getAttribute('data-total-items') || '';
      status.textContent = LABELS.done + (total ? ' (' + total + ' total).' : '.');
    }
  }

  function parsePage(url) {
    try {
      var parsed = new URL(url, window.location.origin);
      return parseInt(parsed.searchParams.get('page'), 10) || 1;
    } catch (e) {
      return 1;
    }
  }

  // -----------------------------------------------------------------
  // Back/forward handling.
  // -----------------------------------------------------------------
  // We check the live DOM for appended cards rather than a module
  // flag, because Symmetry's filter AJAX can wipe appended cards
  // between a Load More click and a popstate. The flag would stay
  // true; the DOM tells the truth.
  window.addEventListener('popstate', function () {
    if (document.querySelector(SELECTORS.freshCard)) {
      window.location.reload();
    }
  });
})();
